import { FC } from 'react';
import { Check, List, Repeat2 } from 'lucide-react';

import { useListGroupResolve } from '@/features/list-groups';

import { ALL_LISTS_CAP, allListsCount, listChoiceOf } from '../../../groups/lists-per-day/list-choice';
import type { GroupRef } from '../../hooks/use-general-times';
import { SlotRankOptions } from './rank/SlotRankOptions';

export const SlotListOptions: FC<{
  group: GroupRef;
  pin?: number;
  /** 19/10/2026 — the drop's rank for this group (all lists). */
  rank?: number;
  active: boolean;
  query: string;
  /** Today's lists of the group, best first (from the plan). */
  today?: string[];
  onSelect: (listId: number | null) => void;
  onRank?: (rank: number) => void;
}> = ({ group, pin, rank, active, query, today = [], onSelect, onRank }) => {
  const resolved = useListGroupResolve(group.id);
  const lists = resolved.data?.lists ?? [];
  const best = allListsCount(group.listsPerDay ?? 0, resolved.data ? lists.length : null) ?? '';
  const automatic =
    listChoiceOf(group.listsPerDay) === 'one'
      ? 'The best list (automatic)'
      : group.allListsMode === 'ALTERNATE'
        ? `The best ${best} take turns (automatic)`
        : `Every one of the best ${best} (automatic)`;
  const all = listChoiceOf(group.listsPerDay) === 'all';
  const ranks = !query.trim() && onRank && all;
  const ranked = all && rank !== undefined; // a rank on a one-list group is not used
  const visible = lists.filter((list) => (list.name ?? `List #${list.wiserId}`).toLowerCase().includes(query.trim().toLowerCase()));
  return (
    <div className="py-1">
      <p className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{group.name}</p>
      {!query.trim() ? (
        <button
          type="button"
          role="option"
          aria-selected={active && !pin && !ranked}
          onClick={() => onSelect(null)}
          className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-xs hover:bg-accent"
        >
          <Repeat2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="flex-1">{automatic}</span>
          {active && !pin && !ranked ? <Check className="h-3.5 w-3.5 text-primary" /> : null}
        </button>
      ) : null}
      {ranks ? <SlotRankOptions groupName={group.name} count={(group.listsPerDay ?? 0) > 0 ? group.listsPerDay! : ALL_LISTS_CAP.max} today={today} rank={rank} onRank={onRank} /> : null}
      {ranks ? <p className="px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Or a fixed list</p> : null}
      {resolved.isLoading ? <p className="px-2 py-3 text-xs text-muted-foreground">Loading lists…</p> : null}
      {resolved.isError ? (
        <button type="button" onClick={() => resolved.refetch()} className="px-2 py-3 text-xs text-destructive">
          Could not load lists. Retry
        </button>
      ) : (
        visible.map((list) => (
          <button
            key={list.wiserId}
            aria-label={list.name ?? `List #${list.wiserId}`}
            type="button"
            role="option"
            aria-selected={pin === list.wiserId}
            onClick={() => onSelect(list.wiserId)}
            className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left hover:bg-accent"
          >
            <List className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1 truncate text-xs" title={list.name ?? `List #${list.wiserId}`}>
              {list.name ?? `List #${list.wiserId}`}
            </span>
            {list.leadCount != null ? (
              <span className="text-[10px] tabular-nums text-muted-foreground">{list.leadCount.toLocaleString()} leads</span>
            ) : null}
            {pin === list.wiserId ? <Check className="h-3.5 w-3.5 text-primary" /> : null}
          </button>
        ))
      )}
      {!resolved.isLoading && !resolved.isError && !visible.length ? (
        <p className="px-2 py-3 text-xs text-muted-foreground">{query ? 'No matching list names.' : 'No lists in this group.'}</p>
      ) : null}
    </div>
  );
};
